import { useEffect, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { displayName, normalizeTimestamp } from '../utils';

export const MAX_COMMENT_LENGTH = 1000;

export function postLocation(post = {}, author = {}) {
  if (post.locationLabel) return post.locationLabel;
  const fromAuthor = author?.locationLabel || [author?.area, author?.city, author?.country].filter(Boolean).join(', ');
  const fromPost = post.authorLocationLabel || [post.authorArea, post.authorCity, post.authorCountry].filter(Boolean).join(', ');
  return fromAuthor || fromPost;
}

export function authorLocationFields(profile = {}) {
  return {
    authorLocationLabel: profile.locationLabel || [profile.area, profile.city, profile.country].filter(Boolean).join(', '),
    authorArea: profile.area || '',
    authorCity: profile.city || '',
    authorCountry: profile.country || '',
  };
}

export function usePostStats(postId, userId) {
  const [stats, setStats] = useState({ likeCount: 0, liked: false, commentCount: 0 });
  useEffect(() => {
    if (!postId) return undefined;
    const stopLikes = onSnapshot(collection(db, 'posts', postId, 'likes'), (snapshot) => setStats((previous) => ({
      ...previous,
      likeCount: snapshot.size,
      liked: Boolean(userId) && snapshot.docs.some((item) => item.id === userId),
    })), (err) => console.warn('Unable to load likes:', err));
    const stopComments = onSnapshot(collection(db, 'posts', postId, 'comments'), (snapshot) => setStats((previous) => ({
      ...previous, commentCount: snapshot.size,
    })), (err) => console.warn('Unable to load comment count:', err));
    return () => { stopLikes(); stopComments(); };
  }, [postId, userId]);
  return stats;
}

export async function setPostLiked(postId, userId, liked) {
  const likeRef = doc(db, 'posts', postId, 'likes', userId);
  if (liked) await setDoc(likeRef, { userId, createdAt: serverTimestamp() });
  else await deleteDoc(likeRef);
}

export function subscribeToComments(postId, onComments, onError) {
  return onSnapshot(collection(db, 'posts', postId, 'comments'), (snapshot) => {
    onComments(snapshot.docs
      .map((item) => ({ id: item.id, ...item.data({ serverTimestamps: 'estimate' }) }))
      .sort((a, b) => normalizeTimestamp(a.createdAt) - normalizeTimestamp(b.createdAt)));
  }, onError);
}

export async function addPostComment(postId, user, profile, text) {
  const value = text.trim();
  if (!value) throw new Error('Write a comment before sending.');
  if (value.length > MAX_COMMENT_LENGTH) throw new Error(`Keep comments under ${MAX_COMMENT_LENGTH} characters.`);
  return addDoc(collection(db, 'posts', postId, 'comments'), {
    userId: user.uid,
    text: value,
    authorUsername: displayName(profile || user),
    authorPhotoURL: profile?.photoURL || '',
    createdAt: serverTimestamp(),
  });
}

export const deletePostComment = (postId, commentId) => deleteDoc(doc(db, 'posts', postId, 'comments', commentId));

const authorCache = new Map();
export function useAuthorProfiles(userIds) {
  const [authors, setAuthors] = useState({});
  const key = [...new Set(userIds.filter(Boolean))].sort().join('|');
  useEffect(() => {
    let cancelled = false;
    const ids = key ? key.split('|') : [];
    Promise.all(ids.map(async (id) => {
      if (!authorCache.has(id)) {
        authorCache.set(id, Promise.resolve().then(() => getDoc(doc(db, 'users', id))).then((snapshot) => (snapshot?.exists() ? snapshot.data() : null)).catch((err) => {
          console.warn('Unable to load author profile:', err);
          authorCache.delete(id);
          return null;
        }));
      }
      return [id, await authorCache.get(id)];
    })).then((entries) => { if (!cancelled) setAuthors(Object.fromEntries(entries.filter(([, value]) => value))); });
    return () => { cancelled = true; };
  }, [key]);
  return authors;
}
